import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Calendar, User, Tag } from "lucide-react";
import AddBlogModal from "../../components/AdminSideComponents/DashBoardFooter/AdminBlog/AddBlogModal";
import EditBlogModal from "../../components/AdminSideComponents/DashBoardFooter/AdminBlog/EditBlogModal";
import axios from "axios";
import DeleteAlertModal from "../../components/AdminSideComponents/DashBoardFooter/Common/DeleteAlertModal";

const BlogCardSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-6">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
        <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-4"></div>
        <div className="flex items-center justify-between">
          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          <div className="h-6 bg-gray-200 rounded-full w-18"></div>
        </div>
      </div>
    </div>
  );
};

const BlogCard = ({ blog, onEdit, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const truncateContent = (content, maxLength = 120) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02] relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`absolute top-4 right-4 z-10 flex gap-2 transition-opacity duration-300 ${
          isHovered ? "opacity-100" : "opacity-0"
        }`}
      >
        <button
          onClick={() => onEdit(blog)}
          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
          title="Edit Blog"
        >
          <Edit size={16} />
        </button>
        <button
          onClick={() => onDelete(blog._id)}
          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
          title="Delete Blog"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="h-48 bg-gray-200 relative overflow-hidden">
        {blog.images && blog.images.length > 0 ? (
          <img
            src={blog.images[0]}
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span className="text-gray-400 text-sm">No Image</span>
          </div>
        )}
      </div>

      {/* Blog Content */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3 line-clamp-2">
          {blog.title}
        </h3>

        <p className="text-gray-600 mb-4 text-sm leading-relaxed">
          {truncateContent(blog.content)}
        </p>

        {/* Author and Date */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <div className="flex items-center gap-1">
            <User size={14} />
            <span>{blog.author}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar size={14} />
            <span>{formatDate(blog.publishedDate)}</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {blog.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
            >
              <Tag size={10} />
              {tag}
            </span>
          ))}
        </div>

        {/* Status Badge */}
        <div className="mt-3">
          <span
            className={`inline-block px-3 py-1 text-xs rounded-full ${
              blog.status === "published"
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {blog.status.charAt(0).toUpperCase() + blog.status.slice(1)}
          </span>
        </div>
      </div>
    </div>
  );
};

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchBlogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/blogs/all-blogs`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setBlogs(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleAddBlog = () => {
    setIsAddModalOpen(true);
  };

  const handleBlogAdded = (newBlog) => {
    setBlogs((prev) => [newBlog, ...prev]);
    fetchBlogs();
  };

  const handleEditBlog = (blog) => {
    setCurrentBlog(blog);
    setIsEditModalOpen(true);
  };

  const handleBlogUpdated = (updatedBlog) => {
    setBlogs((prev) =>
      prev.map((blog) => (blog._id === updatedBlog._id ? updatedBlog : blog))
    );
    setIsEditModalOpen(false);
    setCurrentBlog(null);
  };

  const handleDeleteClick = (blogId) => {
    const blog = blogs.find((b) => b._id === blogId);
    if (blog) {
      setBlogToDelete({
        id: blogId,
        name: blog.title,
      });
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!blogToDelete) return;

    setDeleteLoading(true);
    try {
      let config = {
        method: "delete",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/blogs/delete-blog/${blogToDelete.id}`,
        headers: {},
      };

      await axios.request(config);
      fetchBlogs(); // Refresh the blog list
    } catch (error) {
      console.error("Error deleting blog:", error);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setBlogToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 mt-6 mb-12 px-6">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Blog Management</h1>
          </div>

          <button
            onClick={handleAddBlog}
            className="bg-[#001330] text-white px-4 py-2 rounded-lg hover:bg-[#002244] transition duration-300 flex items-center gap-2"
          >
            <Plus size={20} />
            Add New Blog
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">Error loading blogs</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={fetchBlogs}
              className="mt-2 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <BlogCardSkeleton key={index} />
            ))}
          </div>
        )}

        {/* Blogs Grid */}
        {!loading && !error && (
          <>
            {blogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No blogs found
                </h3>
                <p className="text-gray-500 mb-6">
                  Get started by creating your first blog post
                </p>
                <button
                  onClick={handleAddBlog}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-200"
                >
                  <Plus size={20} />
                  Create First Blog
                </button>
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Blogs</p>
                      <p className="text-2xl font-bold text-gray-800">
                        {blogs.length}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Published</p>
                      <p className="text-2xl font-bold text-green-600">
                        {
                          blogs.filter((blog) => blog.status === "published")
                            .length
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Draft</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {blogs.filter((blog) => blog.status === "draft").length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Blogs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {blogs.map((blog) => (
                    <BlogCard
                      key={blog._id}
                      blog={blog}
                      onEdit={handleEditBlog}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <AddBlogModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onBlogAdded={handleBlogAdded}
      />
      <EditBlogModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCurrentBlog(null);
        }}
        onBlogUpdated={handleBlogUpdated}
        blogId={currentBlog?._id}
        allBlog={fetchBlogs}
      />
      <DeleteAlertModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        dltType="blog"
        dltName={blogToDelete?.name || ""}
        onConfirm={handleConfirmDelete}
        dltLoading={deleteLoading}
      />
    </div>
  );
};

export default AdminBlog;
